import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Users, Smartphone, Heart, HeartHandshake, Quote } from "lucide-react";
import PageLayout from "../components/site/PageLayout";
import { Reveal } from "../components/site/Reveal";
import { SectionHeading } from "../components/site/SectionHeading";
import { StatCounter } from "../components/site/StatCounter";
import { ImgSlot } from "../components/site/ImgSlot";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { useContent } from "../lib/content";

const ICONS = { smartphone: Smartphone, sparkles: Sparkles, users: Users };

const Home = () => {
  const { content } = useContent();
  const c = content;

  return (
    <PageLayout title="Home">
      {/* Hero */}
      <section className="relative pt-16 md:pt-24 pb-24 overflow-hidden">
        <div className="ambient" aria-hidden />
        <div className="ambient-grid" aria-hidden />
        <div className="container-tt relative grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="inline-flex items-center gap-2.5 rounded-full border border-sky/20 bg-white/80 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-skyDark shadow-[0_1px_2px_rgba(15,23,42,.04)]" data-testid="hero-eyebrow">
                <span className="h-1.5 w-1.5 rounded-full bg-sky animate-pulse" /> {c.hero.eyebrow}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 data-testid="hero-title" className="mt-7 font-display text-[clamp(2.6rem,1.6rem+4.4vw,4.4rem)] leading-[1.06] tracking-[-0.018em] text-stone">
                {c.hero.title}
              </h1>
            </Reveal>
            <Reveal delay={0.1}>
              <p data-testid="hero-subtitle" className="mt-6 text-lg md:text-xl leading-relaxed text-muted-foreground max-w-2xl">
                {c.hero.subtitle}
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button asChild className="btn-pill btn-motion btn-sky-fill h-14 px-8 text-base text-white shadow-sky hover:shadow-skyHover border-0">
                  <Link to={c.hero.ctaPrimary.href} data-testid="hero-cta-primary">
                    {c.hero.ctaPrimary.label} <ArrowRight className="ml-2 transition-transform group-hover:translate-x-0.5" size={18} />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="btn-pill btn-motion h-14 px-8 text-base border-stone/15 bg-white/70 text-stone hover:bg-stone hover:border-stone hover:text-white">
                  <Link to={c.hero.ctaSecondary.href} data-testid="hero-cta-secondary">{c.hero.ctaSecondary.label}</Link>
                </Button>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex -space-x-2.5">
                  {[Users, Smartphone, HeartHandshake].map((Icon, i) => (
                    <span key={i} className="h-9 w-9 rounded-full bg-accent text-skyDark ring-2 ring-white grid place-items-center">
                      <Icon size={15} />
                    </span>
                  ))}
                </div>
                <span>Serving seniors across <b className="text-stone font-medium">{c.brand.location}</b>.</span>
              </div>
            </Reveal>
          </div>

          {/* Hero visual bento */}
          <div className="lg:col-span-5">
            <Reveal delay={0.15}>
              <div className="grid grid-cols-6 grid-rows-6 gap-3 h-[440px] sm:h-[520px]">
                <ImgSlot src={c.hero.heroImage} label="Hero photo" ratio="auto" className="col-span-6 row-span-4 h-full shadow-card" />
                <ImgSlot src={c.hero.workshopImage} label="Workshop" ratio="auto" className="col-span-3 row-span-2 h-full shadow-card" />
                <div className="relative col-span-3 row-span-2 rounded-2xl bg-stone text-white p-5 flex flex-col justify-between overflow-hidden shadow-dark grain">
                  <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-sky/25 blur-2xl" aria-hidden />
                  <Heart size={22} className="relative text-sky" />
                  <div className="relative">
                    <div className="font-display text-2xl leading-tight">One senior at a time.</div>
                    <div className="text-sm text-white/70 mt-1">Patience is our core skill.</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Mission strip */}
      <section className="border-y border-border/60 bg-gradient-to-b from-white to-accent/30">
        <div className="container-tt py-16 md:py-20 grid md:grid-cols-3 gap-10">
          <Reveal>
            <div className="eyebrow"><span className="tie" aria-hidden />Why we exist</div>
            <h2 className="font-display text-3xl md:text-4xl mt-4 leading-tight text-stone">{c.mission.title}</h2>
          </Reveal>
          <Reveal delay={0.05}>
            <p className="md:col-span-2 text-lg leading-relaxed text-stone/80">{c.mission.body}</p>
          </Reveal>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24">
        <div className="container-tt">
          <SectionHeading eyebrow="By the numbers" title="Our mission in motion." description="These numbers grow with every workshop we host and every senior center we partner with. Track our long-term journey on the Impact page." />
          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5" data-testid="stats-grid">
            {c.stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.05}>
                <div className="card-surface card-lift p-6 h-full">
                  <span className="tie-top" aria-hidden />
                  <div className="mt-4 font-display text-4xl md:text-[2.75rem] leading-none text-stone">
                    <StatCounter value={s.value} prefix={s.prefix} suffix={s.suffix} />
                  </div>
                  <div className="mt-3 text-sm text-muted-foreground">{s.label}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Programs preview */}
      <section className="py-24 pt-4">
        <div className="container-tt">
          <SectionHeading eyebrow="Programs" title="Meeting seniors where they are." description="Every visit is tailored to the people in the room. Here's how we spend our time." />
          <div className="mt-14 grid md:grid-cols-2 gap-6" data-testid="programs-grid">
            {c.programs.map((p, i) => {
              const Icon = ICONS[p.icon] || Sparkles;
              return (
                <Reveal key={p.slug} delay={i * 0.06}>
                  <Link to="/programs" className="block group card-surface card-lift p-8 h-full" data-testid={`program-card-${p.slug}`}>
                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-accent/70 border border-sky/15 text-skyDark transition-colors group-hover:bg-accent">
                      <Icon size={22} />
                    </div>
                    <h3 className="font-display text-2xl mt-6 text-stone">{p.title}</h3>
                    <p className="mt-3 text-muted-foreground leading-relaxed">{p.summary}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-skyDark font-medium">
                      Learn more <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Volunteer CTA */}
      <section className="py-24 pt-4">
        <div className="container-tt">
          <div className="relative rounded-[2rem] bg-stone text-white p-10 md:p-16 grid md:grid-cols-5 gap-10 items-center overflow-hidden shadow-dark ring-1 ring-white/10 grain">
            <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-sky/20 blur-3xl" aria-hidden />
            <div className="absolute -bottom-32 -left-16 h-72 w-72 rounded-full bg-sky/10 blur-3xl" aria-hidden />
            <div className="md:col-span-3 relative z-[2]">
              <div className="eyebrow !text-sky/90"><span className="tie" aria-hidden />Join TechTies</div>
              <h2 className="font-display text-3xl md:text-[2.9rem] mt-4 leading-[1.08]">Show up for your community — become a TechTies volunteer.</h2>
              <p className="mt-5 text-white/75 text-lg max-w-xl leading-relaxed">Workshops happen when opportunities arise, and every session is hands-on. If you can text your friends and stay patient with your grandparents, you can volunteer with us.</p>
              <div className="mt-9 flex flex-wrap gap-3">
                <Button asChild className="btn-pill btn-motion btn-sky-fill h-14 px-8 text-white shadow-sky hover:shadow-skyHover border-0">
                  <Link to="/volunteer" data-testid="cta-volunteer-apply">Apply now</Link>
                </Button>
                <Button asChild variant="outline" className="btn-pill btn-motion h-14 px-8 border-white/25 bg-transparent text-white hover:bg-white hover:text-stone">
                  <Link to="/about">Meet the founders</Link>
                </Button>
              </div>
            </div>
            <div className="md:col-span-2 relative z-[2]">
              <ImgSlot label="Volunteer team" ratio="4/5" className="h-full" dark />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 pt-4">
        <div className="container-tt">
          <SectionHeading eyebrow="Voices" title="What people say." />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {c.testimonials.map((t, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <figure className="card-surface card-lift p-8 h-full flex flex-col">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent/70 text-skyDark">
                    <Quote size={18} />
                  </span>
                  <blockquote className="mt-5 font-display italic text-[19px] leading-relaxed text-stone/90 flex-1">"{t.quote}"</blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-border/70 flex items-center gap-3">
                    {t.image ? (
                      <img src={t.image} alt={t.author} className="h-11 w-11 rounded-full object-cover ring-2 ring-accent" />
                    ) : (
                      <span aria-hidden className="h-11 w-11 rounded-full bg-accent text-skyDark grid place-items-center font-display text-lg">{(t.author || "?").charAt(0)}</span>
                    )}
                    <span className="text-sm">
                      <div className="font-medium text-stone">{t.author}</div>
                      <div className="text-muted-foreground">{t.role}</div>
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Partners preview */}
      <section className="py-20 border-y border-border/60 bg-gradient-to-b from-white to-accent/30 overflow-hidden">
        <div className="container-tt">
          <div className="flex items-end justify-between flex-wrap gap-4">
            <SectionHeading eyebrow="Partners" title="Where we work." />
            <Link to="/partners" className="group text-skyDark font-medium inline-flex items-center gap-1.5">
              See all <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
        <div className="mt-12 marquee-mask">
          <div className="flex w-max gap-4 animate-marquee motion-reduce:w-full motion-reduce:flex-wrap motion-reduce:justify-center motion-reduce:px-6 hover:[animation-play-state:paused]">
            {[...c.partners, ...c.partners].map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                aria-hidden={i >= c.partners.length || undefined}
                className={`shrink-0 flex items-center gap-3 rounded-full border border-border bg-white px-6 py-3.5 shadow-[0_1px_2px_rgba(15,23,42,.04)] ${i >= c.partners.length ? "motion-reduce:hidden" : ""}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-sky" aria-hidden />
                <span className="font-medium text-stone whitespace-nowrap">{p.name}</span>
                <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">{p.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ preview */}
      <section className="py-24">
        <div className="container-tt grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="FAQ" title="Common questions." description="Have another? Reach us via the Contact page." />
            <Button asChild className="mt-7 btn-pill btn-motion bg-stone hover:bg-sageDark text-white h-12 px-6 shadow-dark">
              <Link to="/faq">All FAQs</Link>
            </Button>
          </div>
          <div className="lg:col-span-7">
            <div className="card-surface px-6 md:px-8">
              <Accordion type="single" collapsible data-testid="home-faq">
                {c.faqs.slice(0, 4).map((f, i) => (
                  <AccordionItem key={i} value={`f-${i}`} className={i === 3 ? "border-b-0" : ""}>
                    <AccordionTrigger className="text-left text-lg py-6 font-display font-medium text-stone hover:no-underline hover:text-skyDark">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-base text-muted-foreground leading-relaxed pb-6">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Home;
